/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  CodeSnippet,
  FormLabel,
  CodeSnippetSkeleton,
  OrderedList,
  ListItem
} from 'carbon-components-react';
import _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';
import { useMount } from 'react-use';

import { copyContentToClipboard } from '@ibm-watson/shared/modules/ia/util/clipboard/clipboard';
import { useAppSelector } from '@ibm-watson/shared/store';
import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { useChannelHelpers } from '../../../hooks/useChannelHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  isSubscribeIntegrationLoading,
  selectDraftIntegration
} from '../../../store/channels/selectors';
import { ChannelContentProps } from '../../../util/types';
import { sharedMessages } from '../../messages';
import { AppRegistrationMessages } from '../app-registration/messages';
import { links } from '../links';
import { FIELDS } from '../schema';
import { EndpointMessages as messages } from './messages';

export const EndpointContent = ({
  assistantId,
  environmentId,
  isQuickSetup
}: ChannelContentProps) => {
  const { formatMessage: t } = useIntl();
  const formMethods = useFormContext();
  const { subscribeForWebhookUrl } = useChannelHelpers(formMethods);
  const { getFieldValue, isInvalidOrEmpty } = useFormHelpers(formMethods);

  const draftIntegration = useAppSelector(selectDraftIntegration, shallowEqual);
  const isSubscribeLoading = useAppSelector(isSubscribeIntegrationLoading);

  useMount(() => {
    if (isInvalidOrEmpty(FIELDS.WEBHOOK_URL) && !_.isNil(assistantId)) {
      subscribeForWebhookUrl(
        assistantId,
        environmentId,
        draftIntegration,
        {},
        FIELDS.WEBHOOK_URL
      );
    }
  });

  return (
    <OrderedList className="TabContent__subtitle TabContent__link-larger-text">
      <ListItem>
        <FormattedMessageWithLink
          url={links.CREATE_BOT_FRAMEWORK_LINK}
          {...messages.descriptionOne}
        />
      </ListItem>
      <ListItem>
        <FormattedMessageWithLink {...messages.descriptionTwo} />
        <div className="TabContent__spacer16" />
        {isQuickSetup && (
          <div>
            <div className="TabContent__spacer8" />
            <FormLabel className="TabContent__field-label">
              <FormattedMessage {...AppRegistrationMessages.appId} />
            </FormLabel>
            <CodeSnippet
              id="teams-app-id"
              feedback={t(sharedMessages.copied)}
              copyButtonDescription={t(sharedMessages.copyToClipboard)}
              copyLabel={t(sharedMessages.copyToClipboard)}
              onClick={() =>
                copyContentToClipboard(getFieldValue(FIELDS.APP_ID))
              }>
              {getFieldValue(FIELDS.APP_ID)}
            </CodeSnippet>
          </div>
        )}
      </ListItem>
      <div className="TabContent__spacer8" />
      <ListItem>
        <FormattedMessageWithLink {...messages.descriptionThree} />
        <div className="TabContent__spacer8" />
        <FormLabel className="TabContent__field-label">
          <FormattedMessage {...messages.generatedEndpointLabel} />
        </FormLabel>
        {isInvalidOrEmpty(FIELDS.WEBHOOK_URL) || isSubscribeLoading ? (
          <CodeSnippetSkeleton />
        ) : (
          <CodeSnippet
            id="teams-generated-callback-url"
            feedback={t(sharedMessages.copied)}
            copyButtonDescription={t(sharedMessages.copyToClipboard)}
            copyLabel={t(sharedMessages.copyToClipboard)}
            onClick={() =>
              copyContentToClipboard(getFieldValue(FIELDS.WEBHOOK_URL))
            }>
            {getFieldValue(FIELDS.WEBHOOK_URL)}
          </CodeSnippet>
        )}
      </ListItem>
      <ListItem>
        <FormattedMessageWithLink {...messages.descriptionFour} />
      </ListItem>
    </OrderedList>
  );
};
